import { FILTERED_GC_CODES, GIFT_CARD_SERVICE_HOST_LOCALE_MAP } from "@/shared/constants";
import { ERROR_CODES, ORDER_STATUSES } from "@/shared/gift-cards.constants";

import { axios } from "@/utils/axios";
import logger from "@/utils/logger";
import { throwOrReturnDefault } from "@/utils/requests";

import {
    Checkout,
    APIGiftCardCheckout,
    APIGiftCardOrderConfirmation,
    GiftCardProps,
    GiftCardResponse,
    APIOrderConfirmation,
    APIOrderStatus,
    OrderStatus,
} from "@/interfaces/gift-cards/gift-card-interface";
import {
    transformGiftCard,
    transformOrderStatus,
} from "@/interfaces/gift-cards/gift-card-transform";

import { Locale } from "@/types/locale";

export const buildGiftCardServiceOrdersBaseUrl = (locale: Locale): string => {
    return `${GIFT_CARD_SERVICE_HOST_LOCALE_MAP[locale]}/external/api/v1/gift-card-orders`;
};

export const buildGiftCardServiceGiftCardsBaseUrl = (locale: Locale): string => {
    return `${GIFT_CARD_SERVICE_HOST_LOCALE_MAP[locale]}/external/api/v1/gift-cards`;
};

export const fetchGiftCards = async (locale: Locale): Promise<Array<GiftCardProps>> => {
    try {
        const giftCardsResponse = await axios(
            `${buildGiftCardServiceGiftCardsBaseUrl(locale)}?locale=${locale}`
        );
        const giftCards = giftCardsResponse.data;
        //  the code is "WALMART" instead of "WALMART_USD"
        const cardsToFilter = FILTERED_GC_CODES; // @TODO temporarily filter cards by request of product

        const filteredGiftCards = giftCards?.data
            .filter((card) => !cardsToFilter.includes(card?.attributes?.sku))
            .map(transformGiftCard);

        // return giftCards?.data?.map(transformGiftCard) ?? null; // @TODO temporarily filter cards by request of product
        return filteredGiftCards ?? null;
    } catch (ex) {
        // This is a fix for the staging, where the gift cards service returns 500 with empty list message. Should not be a block error for rendering the page.
        if (ex?.response?.status === 500 && ex?.response?.data?.message === "List is empty.") {
            return [];
        }
        const errorDescription = `Failed to fetch gift cards locale=${locale}`;
        logger.logException(ex, errorDescription);
        return throwOrReturnDefault(ex);
    }
};

export const giftCardCheckout = async (
    checkout: APIGiftCardCheckout,
    locale: Locale
): Promise<GiftCardResponse<Checkout>> => {
    const checkoutEndpoint = `${buildGiftCardServiceOrdersBaseUrl(
        locale
    )}/external/api/v1/gift-card-orders/afterpay-checkout?locale=${locale}`;
    const checkoutResponse = await axios.post(checkoutEndpoint, { data: checkout });
    return checkoutResponse.data;
};

export const giftCardOrderConfirmation = async (
    token: string,
    giftCardCheckout: APIGiftCardCheckout,
    locale: Locale
): Promise<GiftCardResponse<APIOrderConfirmation>> => {
    const data: APIGiftCardOrderConfirmation = {
        order_token: token,
        gift_card_id: giftCardCheckout.gift_card_id,
        is_gift: giftCardCheckout.is_gift,
        currency: giftCardCheckout.currency,
        consumer_firstname: giftCardCheckout.consumer_firstname,
        consumer_lastname: giftCardCheckout.consumer_lastname,
        consumer_email: giftCardCheckout.consumer_email,
        recipient_name: giftCardCheckout.recipient_name,
        recipient_email: giftCardCheckout.recipient_email,
        recipient_message: giftCardCheckout.recipient_message,
    };
    const orderConfirmationEndpoint = `${buildGiftCardServiceOrdersBaseUrl(locale)}/process`;

    const orderConfirmationResponse = await axios.post(orderConfirmationEndpoint, { data });
    return orderConfirmationResponse.data;
};

/*
 * Function that requests the order status from the backend. Used when we poll for the order status.
 */
export const giftCardOrderStatus = async (
    orderId: number,
    locale: Locale
): Promise<GiftCardResponse<APIOrderStatus>> => {
    const orderStatusEndpoint = `${buildGiftCardServiceOrdersBaseUrl(locale)}/${orderId}/status`;

    const orderStatusResponse = await axios(orderStatusEndpoint);
    return orderStatusResponse.data;
};

export const getOrderStatus = (orderId: number, locale: Locale): Promise<OrderStatus> => {
    return new Promise((resolve, reject) => {
        giftCardOrderStatus(orderId, locale)
            .then(({ data }) => {
                const { error_code: errorCode, order_status: orderStatus } = data;

                if (
                    ERROR_CODES.includes(errorCode) ||
                    orderStatus === ORDER_STATUSES.FULFILLMENT_ERROR
                ) {
                    return reject(
                        new Error(`Order failed with code ${errorCode} and status ${orderStatus}`)
                    );
                }
                resolve(transformOrderStatus(data));
            })
            .catch((err) => reject(new Error(`Order status check failed: ${err}`)));
    });
};

export async function fetchGiftCard(
    giftCardCode: string,
    locale: Locale
): Promise<GiftCardProps | undefined> {
    const url = `${buildGiftCardServiceGiftCardsBaseUrl(locale)}/${giftCardCode}?locale=${locale}`;
    try {
        const response = await axios(url);
        const { data } = response.data;
        if (data !== null) {
            return transformGiftCard(data);
        }
    } catch (ex) {
        const errorDescription = `failed to fetch gift card giftCardCode=${giftCardCode} locale=${locale} url=${url}`;
        logger.logException(ex, errorDescription);
        if (ex?.response?.status !== 404) {
            throw ex;
        }
    }
}
